import React, { Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import MobileMenuButton from "./components/MobileMenuButton";
import ErrorBoundary from "./components/ErrorBoundary";
import { AuthProvider } from "./context/AuthContext";
import { SpotifyProvider } from "./context/SpotifyContext";
import { MiniPlayerProvider } from "./context/MiniPlayerContext";
import { LiveTVMiniPlayerProvider } from "./context/LiveTVMiniPlayerContext";
import { Toaster } from "./components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { useEmailAutoSync } from "./hooks/useEmailAutoSync";
import GlobalMediaPlayerBar from "./components/GlobalMediaPlayerBar";
import SpotifyGlobalSync from "./components/SpotifyGlobalSync";
import MiniVideoPlayer from "./components/MiniVideoPlayer";
import LiveTVMiniPlayer from "./components/LiveTVMiniPlayer";
import NowPlayingModal from "./components/NowPlayingModal";

// Authentication & Utilities
import Login from "./pages/Login";
import SpotifyCallback from "./pages/SpotifyCallback";
import SpotifyTest from "./pages/SpotifyTest";

// Main Dashboard
import Dashboard from "./pages/Dashboard";
import RealEstateManagingPartnerDashboardNew from "./pages/RealEstateManagingPartnerDashboardNew";

// Real Estate Managing Partner Pages
import RealEstateManagingPartnerContacts from "./pages/RealEstateManagingPartnerContacts";
import RealEstateManagingPartnerDocuments from "./pages/RealEstateManagingPartnerDocuments";
import RealEstateManagingPartnerMeetings from "./pages/RealEstateManagingPartnerMeetings";
import RealEstateManagingPartnerCommunications from "./pages/RealEstateManagingPartnerCommunications";
import RealEstateManagingPartnerCalendar from "./pages/RealEstateManagingPartnerCalendar";
import RealEstateManagingPartnerCalendarDaily from "./pages/RealEstateManagingPartnerCalendarDaily";
import RealEstateManagingPartnerAnalytics from "./pages/RealEstateManagingPartnerAnalytics";
import RealEstateManagingPartnerFinances from "./pages/RealEstateManagingPartnerFinances";
import AllBillsPage from "./pages/AllBillsPage";
import AllTransactionsPage from "./pages/AllTransactionsPage";
import RealEstateManagingPartnerMarketing from "./pages/RealEstateManagingPartnerMarketing";
import MarketingHub from "./pages/MarketingHub";
import RealEstateManagingPartnerProperties from "./pages/RealEstateManagingPartnerProperties";
import RealEstateManagingPartnerReports from "./pages/RealEstateManagingPartnerReports";
import SettingsAccounts from "./pages/SettingsAccounts";

// Real Estate Support Pages
import RealEstateMediaPortfolio from "./pages/RealEstateMediaPortfolio";
import UltimateLeadGeneration from "./pages/UltimateLeadGeneration";
import RealEstateClientsCustomers from "./pages/RealEstateClientsCustomers";
import RealEstateCustomerService from "./pages/RealEstateCustomerService";
import RealEstateAssetsNew from "./pages/RealEstateAssetsNew";
import RealEstateDepartments from "./pages/RealEstateDepartments";
import RealEstateEmployees from "./pages/RealEstateEmployees";
import RealEstateHR from "./pages/RealEstateHR";
import RealEstateDistribution from "./pages/RealEstateDistribution";
import RealEstateAnalytics from "./pages/RealEstateAnalytics";
import RealEstateInvestments from "./pages/RealEstateInvestments";

// Shared/Utility Pages
import MediaPortfolio from "./pages/MediaPortfolio";
import MessageCenter from "./pages/MessageCenter";
import EmailCenter from "./pages/EmailCenter";
import Communications from "./pages/Communications";
import OtterAI from "./pages/OtterAI";
import WhisperTranscribe from "./pages/WhisperTranscribe";
import Notes from "./pages/Notes";
import SimpleNotes from "./pages/SimpleNotes";
import MainCalendar from "./pages/MainCalendar";
import Properties from "./pages/Properties";
import Marketing from "./pages/Marketing";
import Distribution from "./pages/Distribution";
import Travel from "./pages/Travel";
import Passwords from "./pages/Passwords";
import MyApps from "./pages/MyApps";
import DocumentsPageWithFolders from "./pages/DocumentsPageWithFolders";
import SocialMedia from "./pages/SocialMedia";
import MediaFactory from "./pages/MediaFactory";

// Lazy load heavy pages
const Meetings = lazy(() => import("./pages/Meetings"));

function App() {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = React.useState(false);
  
  // Enable global email auto-sync
  useEmailAutoSync();

  return (
    <div className="App bg-[#000000] min-h-screen">
      <BrowserRouter>
        <AuthProvider>
          <SpotifyProvider>
            <MiniPlayerProvider>
            <LiveTVMiniPlayerProvider>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/spotify/callback" element={<SpotifyCallback />} />
              <Route path="/spotify-test" element={<SpotifyTest />} />

              {/* Main App Routes */}
              <Route path="/*" element={
                <>
                  <Sidebar 
                    isOpen={isSidebarOpen} 
                    setIsOpen={setIsSidebarOpen}
                    isCollapsed={isSidebarCollapsed}
                    setIsCollapsed={setIsSidebarCollapsed}
                  />
                  <MobileMenuButton onClick={() => setIsSidebarOpen(true)} />
                  <div className={`ml-0 transition-all duration-300 pb-20 ${isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64'}`}>
                    <div>
                      <Routes>
                        {/* Main Dashboard */}
                        <Route path="/" element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
                        <Route path="/dashboard" element={<ErrorBoundary><RealEstateManagingPartnerDashboardNew /></ErrorBoundary>} />
                        <Route path="/dashboard-re" element={<ErrorBoundary><RealEstateManagingPartnerDashboardNew /></ErrorBoundary>} />
                        
                        {/* Real Estate Managing Partner Core Pages */}
                        <Route path="/contacts-re" element={<ErrorBoundary><RealEstateManagingPartnerContacts /></ErrorBoundary>} />
                        <Route path="/documents-re" element={<ErrorBoundary><RealEstateManagingPartnerDocuments /></ErrorBoundary>} />
                        <Route path="/meetings-re" element={<ErrorBoundary><RealEstateManagingPartnerMeetings /></ErrorBoundary>} />
                        <Route path="/communications-re" element={<ErrorBoundary><RealEstateManagingPartnerCommunications /></ErrorBoundary>} />
                        <Route path="/calendar-re" element={<ErrorBoundary><RealEstateManagingPartnerCalendar /></ErrorBoundary>} />
                        <Route path="/calendar-week" element={<ErrorBoundary><RealEstateManagingPartnerCalendarDaily /></ErrorBoundary>} />
                        <Route path="/analytics" element={<ErrorBoundary><RealEstateManagingPartnerAnalytics /></ErrorBoundary>} />
                        <Route path="/finances-re" element={<ErrorBoundary><RealEstateManagingPartnerFinances /></ErrorBoundary>} />
                        <Route path="/all-bills" element={<ErrorBoundary><AllBillsPage /></ErrorBoundary>} />
                        <Route path="/all-transactions" element={<ErrorBoundary><AllTransactionsPage /></ErrorBoundary>} />
                        <Route path="/marketing-re" element={<ErrorBoundary><MarketingHub /></ErrorBoundary>} />
                        <Route path="/properties-re" element={<ErrorBoundary><RealEstateManagingPartnerProperties /></ErrorBoundary>} />
                        <Route path="/reports-re" element={<ErrorBoundary><RealEstateManagingPartnerReports /></ErrorBoundary>} />
                        
                        {/* Real Estate Support Pages */}
                        <Route path="/media-portfolio-re" element={<ErrorBoundary><RealEstateMediaPortfolio /></ErrorBoundary>} />
                        <Route path="/lead-generation" element={<ErrorBoundary><UltimateLeadGeneration /></ErrorBoundary>} />
                        <Route path="/clients-customers-re" element={<ErrorBoundary><RealEstateClientsCustomers /></ErrorBoundary>} />
                        <Route path="/customer-service-re" element={<ErrorBoundary><RealEstateCustomerService /></ErrorBoundary>} />
                        <Route path="/assets-files-re" element={<ErrorBoundary><RealEstateAssetsNew /></ErrorBoundary>} />
                        <Route path="/departments-re" element={<ErrorBoundary><RealEstateDepartments /></ErrorBoundary>} />
                        <Route path="/employees-re" element={<ErrorBoundary><RealEstateEmployees /></ErrorBoundary>} />
                        <Route path="/hr-re" element={<ErrorBoundary><RealEstateHR /></ErrorBoundary>} />
                        <Route path="/distribution-re" element={<ErrorBoundary><RealEstateDistribution /></ErrorBoundary>} />
                        <Route path="/analytics-re" element={<ErrorBoundary><RealEstateAnalytics /></ErrorBoundary>} />
                        <Route path="/investments-re" element={<ErrorBoundary><RealEstateInvestments /></ErrorBoundary>} />
                        
                        {/* Shared/Utility Pages */}
                        <Route path="/media-portfolio" element={<MediaPortfolio />} />
                        <Route path="/message-center" element={<MessageCenter />} />
                        <Route path="/email-center" element={<EmailCenter />} />
                        <Route path="/communications" element={<Communications />} />
                        <Route path="/otter-ai" element={<OtterAI />} />
                        <Route path="/transcribe" element={<WhisperTranscribe />} />
                        <Route path="/notes" element={<Notes />} />
                        <Route path="/quick-notes" element={<SimpleNotes />} />
                        <Route path="/calendar" element={<MainCalendar />} />
                        <Route path="/main-calendar" element={<Navigate to="/calendar" replace />} />
                        <Route path="/properties" element={<Properties />} />
                        <Route path="/marketing" element={<Marketing />} />
                        <Route path="/social-media" element={<SocialMedia />} />
                        <Route path="/media-factory" element={<MediaFactory />} />
                        <Route path="/distribution" element={<Distribution />} />
                        <Route path="/travel" element={<Travel />} />
                        <Route path="/passwords" element={<Passwords />} />
                        <Route path="/my-apps" element={<MyApps />} />
                        <Route path="/documents-page" element={<DocumentsPageWithFolders />} />
                        <Route path="/settings" element={<ErrorBoundary><SettingsAccounts /></ErrorBoundary>} />
                        <Route path="/accounts" element={<Navigate to="/settings" replace />} />
                        
                        {/* Redirect old /contacts to /contacts-re */}
                        <Route path="/contacts" element={<Navigate to="/contacts-re" replace />} />
                        
                        {/* Lazy loaded pages */}
                        <Route path="/meetings" element={
                          <Suspense fallback={
                            <div className="flex items-center justify-center min-h-screen">
                              <div className="text-center">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                                <p className="text-gray-400">Loading meetings...</p>
                              </div>
                            </div>
                          }>
                            <Meetings />
                          </Suspense>
                        } />
                      </Routes>
                    </div>
                  </div>
                </>
              } />
            </Routes>
            
            {/* Global Spotify sync - enables player bar on all pages */}
            <SpotifyGlobalSync />
            
            {/* Global Spotify Player Bar - MediaFactory design */}
            <GlobalMediaPlayerBar />
            
            {/* Persistent Mini Video Player */}
            <MiniVideoPlayer />
            
            {/* Live TV Mini Player - persists across pages */}
            <LiveTVMiniPlayer />
            
            {/* Now Playing Modal with Lyrics */}
            <NowPlayingModal />
          </LiveTVMiniPlayerProvider>
          </MiniPlayerProvider>
          </SpotifyProvider>
        </AuthProvider>
        <Toaster />
        <SonnerToaster richColors position="top-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
